import type { Lang } from "./index";

// Copy for the meal-tracking screens (daily tracker, meal entry by text /
// photo / voice / barcode, confirmation, edit modal, invite chip). Each
// language is written natively (grammar, idiom, tone) — not a literal
// translation. Interpolation and pluralization go through function fields.

export type MealsCopy = {
  // --- daily-tracker-screen ---
  loadError: string;
  deleteError: string;
  favoriteError: string;
  resendError: string;
  resendDone: (email: string) => string;
  verifyBanner: string;
  resending: string;
  resendAgain: string;
  heroRemainingToday: string;
  heroConsumedThatDay: string;
  greetingHello: (name: string) => string;
  toneNoTarget: string;
  toneEmptyDay: string;
  toneBigWindow: string;
  toneMidDay: string;
  toneSmallSpace: string;
  toneGoalClosed: string;
  toneSlightlyOver: string;
  macrosLabel: string;
  macroProtein: string;
  macroFat: string;
  macroCarbs: string;
  mealsToday: string;
  mealsThatDay: string;
  mealCount: (n: number) => string;
  prevDay: string;
  nextDay: string;
  loading: string;
  emptyToday: string;
  emptyThatDay: string;
  addFavoriteAria: string;
  deleteMealAria: (name: string) => string;
  weekdayShort: [string, string, string, string, string, string, string];
  todayTag: string;
  yesterdayTag: string;
  // --- delete confirmation modal ---
  deleteTitle: string;
  // Sentence around the (bold) meal name: rendered as
  // `prefix` + <b>name</b> + `suffix`.
  deleteBodyPrefix: string;
  deleteBodySuffix: string;
  deleting: string;
  deleteConfirm: string;
  deleteBack: string;
  closeAria: string;
  // --- meal-entry-screen ---
  entryBack: string;
  entryTitle: string;
  entryH1: string;
  entryH1Accent: string;
  entryLead: string;
  entryTerminalLabel: string;
  entryPlaceholder: string;
  entryTextareaAria: string;
  entryQuickLabel: string;
  entryQuickInserts: [string, string, string, string, string];
  entryNoFood: string;
  entryGenericError: string;
  entryAnalyzing: string;
  entrySubmit: string;
  // --- meal-photo-screen ---
  photoBack: string;
  photoTitle: string;
  photoH1: string;
  photoLead: string;
  photoNoFood: string;
  photoAnalyzeError: string;
  photoStatusAnalyzing: string;
  photoStatusReady: string;
  photoFrameHint: string;
  photoTips: [string, string, string];
  photoPreviewAlt: string;
  photoManualAria: string;
  photoShutterBusyAria: string;
  photoShutterAria: string;
  photoGalleryAria: string;
  photoBusyCaption: string;
  // --- meal-voice-screen ---
  voiceBack: string;
  voiceTitle: string;
  voiceH1: string;
  voiceLead: string;
  voiceEmptyRecording: string;
  voiceHeard: (text: string) => string;
  voiceNothingHeard: string;
  voiceGenericError: string;
  voiceMicDenied: string;
  voiceMicError: string;
  voiceStatusUploading: string;
  voiceStatusRecording: string;
  voiceStatusIdle: string;
  voiceTranscriptUploading: string;
  voiceTranscriptRecording: string;
  voiceTranscriptIdle: string;
  voiceHintLabel: string;
  voiceHintBody: string;
  voiceUnsupported: string;
  voiceCancelAria: string;
  voiceStopAria: string;
  voiceRecordAria: string;
  voiceDoneAria: string;
  // --- meal-barcode-screen ---
  barcodeBackTitleScanning: string;
  barcodeBackTitleLooking: string;
  barcodeBackTitleManual: string;
  barcodeBackTitleName: string;
  barcodeBack: string;
  barcodeH1: string;
  barcodeLead: string;
  barcodeStatusScanning: string;
  barcodeEanLabel: string;
  barcodeEanPlaceholder: string;
  barcodePortionLabel: string;
  barcodePortionHint: string;
  barcodeCheck: string;
  barcodeNameFallbackNote: string;
  barcodeNameLabel: string;
  barcodeNameHint: string;
  barcodeNamePlaceholder: string;
  barcodeEstimating: string;
  barcodeEstimate: string;
  barcodePortionMeta: string;
  barcodePortionAria: string;
  barcodeLookingEyebrow: string;
  barcodeLookingSource: string;
  barcodeManualToggle: string;
  barcodeScanToggle: string;
  barcodeUnsupportedHint: string;
  barcodeEnterName: string;
  barcodeEstimateFailed: string;
  barcodeGenericError: string;
  barcodeEanInvalid: string;
  barcodeCameraDenied: string;
  barcodeCameraError: string;
  // --- meal-confirmation-screen ---
  confirmBack: string;
  confirmTitle: string;
  confirmThingCount: (n: number) => string;
  confirmCheckHint: string;
  confirmFavoriteError: string;
  confirmGenericError: string;
  confirmFaved: string;
  confirmFaving: string;
  confirmFavorite: string;
  confirmRemove: string;
  confirmRemoveAria: (name: string) => string;
  confirmLessKcalAria: (name: string) => string;
  confirmMoreKcalAria: (name: string) => string;
  confirmEdit: string;
  confirmEditDone: string;
  confirmFieldName: string;
  confirmFieldPortion: string;
  confirmFieldKcal: string;
  confirmFieldProtein: string;
  confirmFieldFat: string;
  confirmFieldCarbs: string;
  confirmIngredientsLabel: string;
  confirmDishSum: string;
  confirmSwap: string;
  confirmSwapPlaceholder: string;
  confirmSwapApply: string;
  confirmSwapping: string;
  confirmSaving: string;
  confirmSaveCta: (n: number, kcal: number) => string;
  confirmAddItem: string;
  confirmAddItemLabel: string;
  confirmAddItemPlaceholder: string;
  confirmAddItemSubmit: string;
  confirmAddItemPending: string;
  confirmAddItemCancel: string;
  confirmAddItemEmpty: string;
  confirmAddItemError: string;
  confirmAddItemMulti: (n: number) => string;
  confirmAddItemLimit: string;
  confirmAddItemFallback: string;
  confirmAddIng: string;
  confirmAddIngLabel: string;
  confirmAddIngPlaceholder: string;
  confirmAddIngSubmit: string;
  confirmAddIngPending: string;
  confirmAddIngCancel: string;
  confirmAddIngEmpty: string;
  confirmAddIngError: string;
  confirmAddIngMulti: (n: number) => string;
  confirmAddIngLimit: string;
  confirmAddIngFallback: string;
  // Per-flat-item AI swap (replaces items[idx] via Syto Sense). Distinct
  // from confirmSwap* (those are for ingredient-level swap inside a dish).
  confirmItemSwap: string;
  confirmItemSwapAria: (name: string) => string;
  confirmItemSwapLabel: string;
  confirmItemSwapPlaceholder: string;
  confirmItemSwapSubmit: string;
  confirmItemSwapPending: string;
  confirmItemSwapCancel: string;
  confirmItemSwapEmpty: string;
  confirmItemSwapError: string;
  confirmItemSwapFallback: string;
  // --- edit-meal-modal ---
  editTitle: string;
  editNameLabel: string;
  editKcalLabel: string;
  editProteinLabel: string;
  editFatLabel: string;
  editCarbsLabel: string;
  editGramPlaceholder: string;
  editGenericError: string;
  editSaving: string;
  editSave: string;
  editCancel: string;
  // --- invite-chip ---
  inviteCodeLabel: string;
  inviteCount: (n: number) => string;
  inviteCopied: string;
  inviteShare: string;
  inviteShareText: string;
};

const pl: MealsCopy = {
  loadError: "Nie udało się załadować",
  deleteError: "Nie udało się usunąć",
  favoriteError: "Nie udało się dodać do ulubionych",
  resendError: "Nie udało się wysłać",
  resendDone: (email) => `Wysłaliśmy nowy link na ${email}`,
  verifyBanner: "Potwierdź email. Wysłaliśmy link na",
  resending: "Wysyłam...",
  resendAgain: "Wyślij ponownie",
  heroRemainingToday: "Pozostało dziś",
  heroConsumedThatDay: "Spożyte tego dnia",
  greetingHello: (name) => `Cześć ${name} — `,
  toneNoTarget: "Ustaw cel, żeby zobaczyć ile zostało.",
  toneEmptyDay: "Pusty dzień. Pierwszy posiłek czeka.",
  toneBigWindow: "Spore okno na obiad. Lodówka pełna, idź.",
  toneMidDay: "Średni dzień, plan trzyma.",
  toneSmallSpace: "Mała przestrzeń, ale jest.",
  toneGoalClosed: "Cel domknięty.",
  toneSlightlyOver: "Lekko ponad. Bywa, jutro nowy dzień.",
  macrosLabel: "Makro",
  macroProtein: "Białko",
  macroFat: "Tłuszcz",
  macroCarbs: "Węgle",
  mealsToday: "Dziś",
  mealsThatDay: "Tego dnia",
  mealCount: (n) => (n === 1 ? "posiłek" : "posiłków"),
  prevDay: "← Wczoraj",
  nextDay: "Następny →",
  loading: "Ładuję...",
  emptyToday: "Jeszcze nic. Dotknij + na dole.",
  emptyThatDay: "Nic nie zalogowano.",
  addFavoriteAria: "dodaj do ulubionych",
  deleteMealAria: (name) => `usuń ${name}`,
  weekdayShort: ["NDZ", "PON", "WT", "ŚR", "CZW", "PT", "SOB"],
  todayTag: "DZIŚ",
  yesterdayTag: "WCZORAJ",
  deleteTitle: "Usuwam ten posiłek?",
  deleteBodyPrefix: "",
  deleteBodySuffix: " zniknie z dziennika i z licznika kcal.",
  deleting: "Usuwam...",
  deleteConfirm: "Usuń",
  deleteBack: "Wróć",
  closeAria: "Zamknij",
  entryBack: "Dziennik",
  entryTitle: "WPISZ",
  entryH1: "Co jadłaś",
  entryH1Accent: "?",
  entryLead: 'Pisz jak do znajomego. Działa "dwa jajka i chleb".',
  entryTerminalLabel: "$ syto › wpisz",
  entryPlaceholder: "owsianka 60g z mlekiem 1,5%, łyżka miodu, garść borówek",
  entryTextareaAria: "Opis posiłku",
  entryQuickLabel: "Często wpisujesz",
  entryQuickInserts: ["łyżka oliwy", "kawa z mlekiem", "jajko sadzone", "jogurt 150g", "banan"],
  entryNoFood:
    "Nie znalazłem jedzenia w opisie. Spróbuj bardziej konkretnie, np. '200g piersi z kurczaka, łyżka ryżu'.",
  entryGenericError: "Coś nie wyszło",
  entryAnalyzing: "ANALIZUJĘ...",
  entrySubmit: "PRZEANALIZUJ I ZAPISZ",
  photoBack: "Dziennik",
  photoTitle: "ZDJĘCIE",
  photoH1: "Pstryk.",
  photoLead: "Talerz z góry, w dobrym świetle. Reszta nasza.",
  photoNoFood: "Nie znalazłem jedzenia na zdjęciu. Spróbuj kadr z talerzem albo wpisz ręcznie.",
  photoAnalyzeError: "Nie udało się przeanalizować",
  photoStatusAnalyzing: "● ANALIZUJĘ · SYTO SENSE",
  photoStatusReady: "● GOTOWY · SYTO SENSE",
  photoFrameHint: "ETYKIETY PRODUKTÓW DAJĄ NAM WIĘCEJ DO PRACY",
  photoTips: ["Dobre światło", "Z góry", "Cały talerz"],
  photoPreviewAlt: "podgląd posiłku",
  photoManualAria: "Wpisz ręcznie",
  photoShutterBusyAria: "Patrzymy na talerz",
  photoShutterAria: "Zrób zdjęcie",
  photoGalleryAria: "Wybierz z galerii",
  photoBusyCaption: "Patrzymy na talerz...",
  voiceBack: "Dziennik",
  voiceTitle: "MÓW",
  voiceH1: "Mów",
  voiceLead: 'Naturalnie. "Zjadłem dwa jajka, kromkę chleba i pomarańczę".',
  voiceEmptyRecording: "Puste nagranie, spróbuj jeszcze raz",
  voiceHeard: (text) => `Usłyszałem "${text}" — nie znalazłem jedzenia. Spróbuj wyraźniej.`,
  voiceNothingHeard: "Nic nie usłyszałem, spróbuj jeszcze raz.",
  voiceGenericError: "Coś nie wyszło",
  voiceMicDenied: "Potrzebujemy dostępu do mikrofonu, daj go w ustawieniach przeglądarki.",
  voiceMicError: "Nie udało się włączyć mikrofonu",
  voiceStatusUploading: "● PRZESYŁAM · SYTO SENSE",
  voiceStatusRecording: "● REC · SYTO SENSE",
  voiceStatusIdle: "○ SYTO SENSE",
  voiceTranscriptUploading: "Słucham nagranie...",
  voiceTranscriptRecording: "nagrywam...",
  voiceTranscriptIdle: "wciśnij mikrofon i mów...",
  voiceHintLabel: "WSKAZÓWKA",
  voiceHintBody:
    'Mów wolniej dla liczb i miar ("dwie stówy gramów"). Syto Sense świetnie ogarnia polski.',
  voiceUnsupported: "Twoja przeglądarka nie wspiera nagrywania.",
  voiceCancelAria: "Anuluj",
  voiceStopAria: "Zatrzymaj nagrywanie",
  voiceRecordAria: "Nagrywaj",
  voiceDoneAria: "Gotowe",
  barcodeBackTitleScanning: "KOD KRESKOWY",
  barcodeBackTitleLooking: "SZUKAM",
  barcodeBackTitleManual: "EAN",
  barcodeBackTitleName: "NAZWA",
  barcodeBack: "Dziennik",
  barcodeH1: "Skanuj",
  barcodeLead: "Open Food Facts ma 3M+ produktów. Jak nie znajdziemy, wpiszesz ręcznie.",
  barcodeStatusScanning: "● SKANUJĘ · OFF API",
  barcodeEanLabel: "Kod EAN",
  barcodeEanPlaceholder: "np. 5901234567890",
  barcodePortionLabel: "Porcja (gramy)",
  barcodePortionHint: "Wartości w bazie są na 100 g; tu wpiszesz ile naprawdę zjadłeś.",
  barcodeCheck: "SPRAWDŹ",
  barcodeNameFallbackNote:
    "Tego kodu nie ma w żadnej z baz. Wpisz nazwę produktu — AI oszacuje wartości z nazwy.",
  barcodeNameLabel: "Nazwa produktu",
  barcodeNameHint: "Im konkretniej, tym lepiej (marka, smak, gramatura).",
  barcodeNamePlaceholder: "np. jogurt naturalny Piątnica 200 g",
  barcodeEstimating: "SZACUJĘ...",
  barcodeEstimate: "OSZACUJ →",
  barcodePortionMeta: "PORCJA (G)",
  barcodePortionAria: "Porcja w gramach",
  barcodeLookingEyebrow: "Szukam w bazie",
  barcodeLookingSource: "Open Food Facts...",
  barcodeManualToggle: "WPISZ EAN RĘCZNIE",
  barcodeScanToggle: "SKANUJ KAMERĄ",
  barcodeUnsupportedHint: "Twoja przeglądarka nie wspiera skanowania, wpisz ręcznie.",
  barcodeEnterName: "Wpisz nazwę produktu",
  barcodeEstimateFailed: "Nie udało się oszacować. Spróbuj dokładniejszej nazwy.",
  barcodeGenericError: "Coś nie wyszło",
  barcodeEanInvalid: "EAN musi mieć 8-14 cyfr",
  barcodeCameraDenied: "Potrzebujemy dostępu do kamery.",
  barcodeCameraError: "Nie mogę włączyć kamery, wpisz EAN ręcznie.",
  confirmBack: "Wróć",
  confirmTitle: "POTWIERDŹ",
  confirmThingCount: (n) => (n === 1 ? "rzecz" : "rzeczy"),
  confirmCheckHint: " · sprawdź zanim zapiszesz",
  confirmFavoriteError: "Nie udało się zapisać",
  confirmGenericError: "Coś nie wyszło",
  confirmFaved: "W ulubionych",
  confirmFaving: "Zapisuję...",
  confirmFavorite: "Ulubione",
  confirmRemove: "Usuń",
  confirmRemoveAria: (name) => `Usuń ${name}`,
  confirmLessKcalAria: (name) => `Mniej kcal: ${name}`,
  confirmMoreKcalAria: (name) => `Więcej kcal: ${name}`,
  confirmEdit: "Popraw",
  confirmEditDone: "Gotowe",
  confirmFieldName: "Nazwa",
  confirmFieldPortion: "Porcja (g)",
  confirmFieldKcal: "Kcal",
  confirmFieldProtein: "Białko (g)",
  confirmFieldFat: "Tłuszcz (g)",
  confirmFieldCarbs: "Węgle (g)",
  confirmIngredientsLabel: "Składniki",
  confirmDishSum: "suma składników",
  confirmSwap: "Zamień",
  confirmSwapPlaceholder: "nowy składnik, np. chleb chrupki",
  confirmSwapApply: "Przelicz",
  confirmSwapping: "Liczę...",
  confirmSaving: "ZAPISUJĘ...",
  confirmSaveCta: (n, kcal) => `ZAPISZ ${n} ${n === 1 ? "POZYCJĘ" : "POZYCJI"} · ${kcal} KCAL`,
  confirmAddItem: "Dodaj brakującą pozycję",
  confirmAddItemLabel: "Co dorzucić",
  confirmAddItemPlaceholder: "np. kawa z mlekiem 200 ml",
  confirmAddItemSubmit: "Dodaj",
  confirmAddItemPending: "Dodaję...",
  confirmAddItemCancel: "Anuluj",
  confirmAddItemEmpty: "Nie rozpoznałem jedzenia. Spróbuj inaczej, np. '150 g ryżu'.",
  confirmAddItemError: "Coś nie wyszło, spróbuj ponownie",
  confirmAddItemMulti: (n) => `Dodano ${n} pozycji`,
  confirmAddItemLimit: "Limit dodawania osiągnięty. Zapisz i wpisz resztę osobno.",
  confirmAddItemFallback: "Wpisz ręcznie",
  confirmAddIng: "Dodaj brakujący składnik",
  confirmAddIngLabel: "Co dorzucić do dania",
  confirmAddIngPlaceholder: "np. łyżka oliwy, 50 g sera",
  confirmAddIngSubmit: "Dodaj",
  confirmAddIngPending: "Dodaję...",
  confirmAddIngCancel: "Anuluj",
  confirmAddIngEmpty: "Nie rozpoznałem składnika. Spróbuj inaczej, np. 50 g sera.",
  confirmAddIngError: "Coś nie wyszło, spróbuj ponownie",
  confirmAddIngMulti: (n) => `Dodano ${n} ${n === 1 ? "składnik" : n < 5 ? "składniki" : "składników"}`,
  confirmAddIngLimit: "Limit składników w tym daniu osiągnięty.",
  confirmAddIngFallback: "Dodaj ręcznie",
  confirmItemSwap: "Zamień",
  confirmItemSwapAria: (name) => `Zamień ${name} na coś innego`,
  confirmItemSwapLabel: "Na co zamienić",
  confirmItemSwapPlaceholder: "np. jajka gotowane 2 szt.",
  confirmItemSwapSubmit: "Przelicz",
  confirmItemSwapPending: "Liczę...",
  confirmItemSwapCancel: "Anuluj",
  confirmItemSwapEmpty: "Nie rozpoznałem jedzenia. Spróbuj inaczej, np. '150 g ryżu'.",
  confirmItemSwapError: "Coś nie wyszło, spróbuj ponownie",
  confirmItemSwapFallback: "Zamień ręcznie",
  editTitle: "Popraw posiłek",
  editNameLabel: "Nazwa",
  editKcalLabel: "Kalorie",
  editProteinLabel: "białko",
  editFatLabel: "tłuszcz",
  editCarbsLabel: "węgle",
  editGramPlaceholder: "g",
  editGenericError: "Coś nie wyszło",
  editSaving: "Zapisuję...",
  editSave: "Zapisz",
  editCancel: "Anuluj",
  inviteCodeLabel: "Twój kod",
  inviteCount: (n) => (n === 1 ? "zaprosz." : "zapr."),
  inviteCopied: "Skopiowano ✓",
  inviteShare: "Udostępnij ↗",
  inviteShareText: "Liczę kalorie w Sytofood. Wejdź na moim kodzie:",
};

const en: MealsCopy = {
  loadError: "Couldn't load this",
  deleteError: "Couldn't delete this",
  favoriteError: "Couldn't add to favorites",
  resendError: "Couldn't send",
  resendDone: (email) => `We sent a fresh link to ${email}`,
  verifyBanner: "Confirm your email. We sent a link to",
  resending: "Sending...",
  resendAgain: "Send again",
  heroRemainingToday: "Left today",
  heroConsumedThatDay: "Eaten that day",
  greetingHello: (name) => `Hi ${name}, `,
  toneNoTarget: "Set a goal to see what's left.",
  toneEmptyDay: "Empty day. Your first meal is waiting.",
  toneBigWindow: "Plenty of room for dinner. Fridge is full, go.",
  toneMidDay: "An average day, the plan holds.",
  toneSmallSpace: "Not much space left, but there is some.",
  toneGoalClosed: "Goal wrapped up.",
  toneSlightlyOver: "A touch over. It happens, tomorrow is a new day.",
  macrosLabel: "Macros",
  macroProtein: "Protein",
  macroFat: "Fat",
  macroCarbs: "Carbs",
  mealsToday: "Today",
  mealsThatDay: "That day",
  mealCount: (n) => (n === 1 ? "meal" : "meals"),
  prevDay: "← Yesterday",
  nextDay: "Next →",
  loading: "Loading...",
  emptyToday: "Nothing yet. Tap + at the bottom.",
  emptyThatDay: "Nothing logged.",
  addFavoriteAria: "add to favorites",
  deleteMealAria: (name) => `delete ${name}`,
  weekdayShort: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
  todayTag: "TODAY",
  yesterdayTag: "YESTERDAY",
  deleteTitle: "Delete this meal?",
  deleteBodyPrefix: "",
  deleteBodySuffix: " will disappear from the diary and the kcal counter.",
  deleting: "Deleting...",
  deleteConfirm: "Delete",
  deleteBack: "Back",
  closeAria: "Close",
  entryBack: "Diary",
  entryTitle: "TYPE",
  entryH1: "What did you eat",
  entryH1Accent: "?",
  entryLead: 'Write like you would to a friend. "Two eggs and bread" works.',
  entryTerminalLabel: "$ syto › type",
  entryPlaceholder: "60g oats with 1.5% milk, a spoon of honey, a handful of blueberries",
  entryTextareaAria: "Meal description",
  entryQuickLabel: "You type these a lot",
  entryQuickInserts: ["spoon of oil", "coffee with milk", "fried egg", "yogurt 150g", "banana"],
  entryNoFood:
    "I couldn't find any food in the description. Try something more specific, e.g. '200g chicken breast, a spoon of rice'.",
  entryGenericError: "Something went wrong",
  entryAnalyzing: "ANALYZING...",
  entrySubmit: "ANALYZE AND SAVE",
  photoBack: "Diary",
  photoTitle: "PHOTO",
  photoH1: "Snap.",
  photoLead: "Plate from above, in good light. We'll take it from there.",
  photoNoFood: "I couldn't find any food in the photo. Try a shot of the plate, or type it in.",
  photoAnalyzeError: "Couldn't analyze this",
  photoStatusAnalyzing: "● ANALYZING · SYTO SENSE",
  photoStatusReady: "● READY · SYTO SENSE",
  photoFrameHint: "PRODUCT LABELS GIVE US MORE TO WORK WITH",
  photoTips: ["Good light", "From above", "Whole plate"],
  photoPreviewAlt: "meal preview",
  photoManualAria: "Type it in",
  photoShutterBusyAria: "Looking at the plate",
  photoShutterAria: "Take a photo",
  photoGalleryAria: "Pick from gallery",
  photoBusyCaption: "Looking at the plate...",
  voiceBack: "Diary",
  voiceTitle: "SPEAK",
  voiceH1: "Speak",
  voiceLead: 'Naturally. "I ate two eggs, a slice of bread and an orange".',
  voiceEmptyRecording: "Empty recording, give it another go",
  voiceHeard: (text) => `I heard "${text}", but found no food. Try speaking more clearly.`,
  voiceNothingHeard: "I didn't hear anything, give it another go.",
  voiceGenericError: "Something went wrong",
  voiceMicDenied: "We need microphone access, grant it in your browser settings.",
  voiceMicError: "Couldn't turn on the microphone",
  voiceStatusUploading: "● UPLOADING · SYTO SENSE",
  voiceStatusRecording: "● REC · SYTO SENSE",
  voiceStatusIdle: "○ SYTO SENSE",
  voiceTranscriptUploading: "Listening to the recording...",
  voiceTranscriptRecording: "recording...",
  voiceTranscriptIdle: "press the mic and speak...",
  voiceHintLabel: "TIP",
  voiceHintBody:
    'Slow down for numbers and amounts ("two hundred grams"). Syto Sense handles speech well.',
  voiceUnsupported: "Your browser doesn't support recording.",
  voiceCancelAria: "Cancel",
  voiceStopAria: "Stop recording",
  voiceRecordAria: "Record",
  voiceDoneAria: "Done",
  barcodeBackTitleScanning: "BARCODE",
  barcodeBackTitleLooking: "SEARCHING",
  barcodeBackTitleManual: "EAN",
  barcodeBackTitleName: "NAME",
  barcodeBack: "Diary",
  barcodeH1: "Scan",
  barcodeLead: "Open Food Facts has 3M+ products. If we can't find it, you can type it in.",
  barcodeStatusScanning: "● SCANNING · OFF API",
  barcodeEanLabel: "EAN code",
  barcodeEanPlaceholder: "e.g. 5901234567890",
  barcodePortionLabel: "Portion (grams)",
  barcodePortionHint: "Database values are per 100 g; here you enter how much you actually ate.",
  barcodeCheck: "CHECK",
  barcodeNameFallbackNote:
    "This code isn't in any of the databases. Type the product name and AI will estimate the values from it.",
  barcodeNameLabel: "Product name",
  barcodeNameHint: "The more specific, the better (brand, flavor, weight).",
  barcodeNamePlaceholder: "e.g. plain yogurt Piątnica 200 g",
  barcodeEstimating: "ESTIMATING...",
  barcodeEstimate: "ESTIMATE →",
  barcodePortionMeta: "PORTION (G)",
  barcodePortionAria: "Portion in grams",
  barcodeLookingEyebrow: "Searching the database",
  barcodeLookingSource: "Open Food Facts...",
  barcodeManualToggle: "ENTER EAN MANUALLY",
  barcodeScanToggle: "SCAN WITH CAMERA",
  barcodeUnsupportedHint: "Your browser doesn't support scanning, enter it manually.",
  barcodeEnterName: "Enter the product name",
  barcodeEstimateFailed: "Couldn't estimate that. Try a more precise name.",
  barcodeGenericError: "Something went wrong",
  barcodeEanInvalid: "An EAN must be 8-14 digits",
  barcodeCameraDenied: "We need camera access.",
  barcodeCameraError: "Can't turn on the camera, enter the EAN manually.",
  confirmBack: "Back",
  confirmTitle: "CONFIRM",
  confirmThingCount: (n) => (n === 1 ? "item" : "items"),
  confirmCheckHint: " · check before you save",
  confirmFavoriteError: "Couldn't save this",
  confirmGenericError: "Something went wrong",
  confirmFaved: "In favorites",
  confirmFaving: "Saving...",
  confirmFavorite: "Favorite",
  confirmRemove: "Remove",
  confirmRemoveAria: (name) => `Remove ${name}`,
  confirmLessKcalAria: (name) => `Fewer kcal: ${name}`,
  confirmMoreKcalAria: (name) => `More kcal: ${name}`,
  confirmEdit: "Edit",
  confirmEditDone: "Done",
  confirmFieldName: "Name",
  confirmFieldPortion: "Portion (g)",
  confirmFieldKcal: "Kcal",
  confirmFieldProtein: "Protein (g)",
  confirmFieldFat: "Fat (g)",
  confirmFieldCarbs: "Carbs (g)",
  confirmIngredientsLabel: "Ingredients",
  confirmDishSum: "sum of ingredients",
  confirmSwap: "Swap",
  confirmSwapPlaceholder: "new ingredient, e.g. crispbread",
  confirmSwapApply: "Recalc",
  confirmSwapping: "Calculating...",
  confirmSaving: "SAVING...",
  confirmSaveCta: (n, kcal) => `SAVE ${n} ${n === 1 ? "ITEM" : "ITEMS"} · ${kcal} KCAL`,
  confirmAddItem: "Add a missing item",
  confirmAddItemLabel: "What to add",
  confirmAddItemPlaceholder: "e.g. coffee with milk 200 ml",
  confirmAddItemSubmit: "Add",
  confirmAddItemPending: "Adding...",
  confirmAddItemCancel: "Cancel",
  confirmAddItemEmpty: "Couldn't recognize any food. Try something like '150 g rice'.",
  confirmAddItemError: "Something went wrong, try again",
  confirmAddItemMulti: (n) => `Added ${n} items`,
  confirmAddItemLimit: "Add limit reached. Save what you have and add the rest in the next meal.",
  confirmAddItemFallback: "Type it in manually",
  confirmAddIng: "Add a missing ingredient",
  confirmAddIngLabel: "What to add to this dish",
  confirmAddIngPlaceholder: "e.g. a spoon of oil, 50 g cheese",
  confirmAddIngSubmit: "Add",
  confirmAddIngPending: "Adding...",
  confirmAddIngCancel: "Cancel",
  confirmAddIngEmpty: "Couldn't recognize that ingredient. Try something like '50 g cheese'.",
  confirmAddIngError: "Something went wrong, try again",
  confirmAddIngMulti: (n) => `Added ${n} ${n === 1 ? "ingredient" : "ingredients"}`,
  confirmAddIngLimit: "Ingredient limit for this dish reached.",
  confirmAddIngFallback: "Add manually",
  confirmItemSwap: "Swap",
  confirmItemSwapAria: (name) => `Swap ${name} for something else`,
  confirmItemSwapLabel: "Swap to what",
  confirmItemSwapPlaceholder: "e.g. boiled eggs x2",
  confirmItemSwapSubmit: "Recalc",
  confirmItemSwapPending: "Calculating...",
  confirmItemSwapCancel: "Cancel",
  confirmItemSwapEmpty: "Couldn't recognize any food. Try something like '150 g rice'.",
  confirmItemSwapError: "Something went wrong, try again",
  confirmItemSwapFallback: "Swap manually",
  editTitle: "Fix the meal",
  editNameLabel: "Name",
  editKcalLabel: "Calories",
  editProteinLabel: "protein",
  editFatLabel: "fat",
  editCarbsLabel: "carbs",
  editGramPlaceholder: "g",
  editGenericError: "Something went wrong",
  editSaving: "Saving...",
  editSave: "Save",
  editCancel: "Cancel",
  inviteCodeLabel: "Your code",
  inviteCount: (n) => (n === 1 ? "invite" : "invites"),
  inviteCopied: "Copied ✓",
  inviteShare: "Share ↗",
  inviteShareText: "I count calories in Sytofood. Join with my code:",
};

const de: MealsCopy = {
  loadError: "Laden fehlgeschlagen",
  deleteError: "Löschen fehlgeschlagen",
  favoriteError: "Konnte nicht zu Favoriten hinzufügen",
  resendError: "Senden fehlgeschlagen",
  resendDone: (email) => `Wir haben einen neuen Link an ${email} geschickt`,
  verifyBanner: "Bestätige deine E-Mail. Wir haben einen Link geschickt an",
  resending: "Senden...",
  resendAgain: "Erneut senden",
  heroRemainingToday: "Heute übrig",
  heroConsumedThatDay: "An dem Tag gegessen",
  greetingHello: (name) => `Hallo ${name}, `,
  toneNoTarget: "Leg ein Ziel fest, um zu sehen, was übrig ist.",
  toneEmptyDay: "Leerer Tag. Die erste Mahlzeit wartet.",
  toneBigWindow: "Reichlich Platz fürs Abendessen. Kühlschrank ist voll, los.",
  toneMidDay: "Ein durchschnittlicher Tag, der Plan hält.",
  toneSmallSpace: "Wenig Spielraum, aber es ist welcher da.",
  toneGoalClosed: "Ziel abgeschlossen.",
  toneSlightlyOver: "Leicht drüber. Kommt vor, morgen ist ein neuer Tag.",
  macrosLabel: "Makros",
  macroProtein: "Eiweiß",
  macroFat: "Fett",
  macroCarbs: "Kohlenhydrate",
  mealsToday: "Heute",
  mealsThatDay: "An dem Tag",
  mealCount: (n) => (n === 1 ? "Mahlzeit" : "Mahlzeiten"),
  prevDay: "← Gestern",
  nextDay: "Weiter →",
  loading: "Lädt...",
  emptyToday: "Noch nichts. Tippe unten auf +.",
  emptyThatDay: "Nichts erfasst.",
  addFavoriteAria: "zu Favoriten hinzufügen",
  deleteMealAria: (name) => `${name} löschen`,
  weekdayShort: ["SO", "MO", "DI", "MI", "DO", "FR", "SA"],
  todayTag: "HEUTE",
  yesterdayTag: "GESTERN",
  deleteTitle: "Diese Mahlzeit löschen?",
  deleteBodyPrefix: "",
  deleteBodySuffix: " verschwindet aus dem Tagebuch und vom kcal-Zähler.",
  deleting: "Löschen...",
  deleteConfirm: "Löschen",
  deleteBack: "Zurück",
  closeAria: "Schließen",
  entryBack: "Tagebuch",
  entryTitle: "EINGEBEN",
  entryH1: "Was hast du gegessen",
  entryH1Accent: "?",
  entryLead: 'Schreib wie einem Freund. "Zwei Eier und Brot" funktioniert.',
  entryTerminalLabel: "$ syto › eingeben",
  entryPlaceholder: "60g Haferflocken mit 1,5% Milch, ein Löffel Honig, eine Handvoll Heidelbeeren",
  entryTextareaAria: "Beschreibung der Mahlzeit",
  entryQuickLabel: "Tippst du oft",
  entryQuickInserts: [
    "Löffel Öl",
    "Kaffee mit Milch",
    "Spiegelei",
    "Joghurt 150g",
    "Banane",
  ],
  entryNoFood:
    "Ich habe in der Beschreibung kein Essen gefunden. Versuch es konkreter, z. B. '200g Hähnchenbrust, ein Löffel Reis'.",
  entryGenericError: "Etwas ist schiefgelaufen",
  entryAnalyzing: "ANALYSIERE...",
  entrySubmit: "ANALYSIEREN UND SPEICHERN",
  photoBack: "Tagebuch",
  photoTitle: "FOTO",
  photoH1: "Klick.",
  photoLead: "Teller von oben, bei gutem Licht. Den Rest machen wir.",
  photoNoFood:
    "Ich habe auf dem Foto kein Essen gefunden. Versuch eine Aufnahme des Tellers oder gib es manuell ein.",
  photoAnalyzeError: "Analyse fehlgeschlagen",
  photoStatusAnalyzing: "● ANALYSIERE · SYTO SENSE",
  photoStatusReady: "● BEREIT · SYTO SENSE",
  photoFrameHint: "PRODUKTETIKETTEN GEBEN UNS MEHR ZU TUN",
  photoTips: ["Gutes Licht", "Von oben", "Ganzer Teller"],
  photoPreviewAlt: "Vorschau der Mahlzeit",
  photoManualAria: "Manuell eingeben",
  photoShutterBusyAria: "Wir schauen auf den Teller",
  photoShutterAria: "Foto machen",
  photoGalleryAria: "Aus Galerie wählen",
  photoBusyCaption: "Wir schauen auf den Teller...",
  voiceBack: "Tagebuch",
  voiceTitle: "SPRECHEN",
  voiceH1: "Sprich",
  voiceLead: 'Ganz natürlich. "Ich habe zwei Eier, eine Scheibe Brot und eine Orange gegessen".',
  voiceEmptyRecording: "Leere Aufnahme, versuch es noch einmal",
  voiceHeard: (text) => `Ich habe "${text}" gehört, aber kein Essen gefunden. Sprich deutlicher.`,
  voiceNothingHeard: "Ich habe nichts gehört, versuch es noch einmal.",
  voiceGenericError: "Etwas ist schiefgelaufen",
  voiceMicDenied: "Wir brauchen Zugriff aufs Mikrofon, erlaube ihn in den Browsereinstellungen.",
  voiceMicError: "Mikrofon konnte nicht eingeschaltet werden",
  voiceStatusUploading: "● ÜBERTRAGE · SYTO SENSE",
  voiceStatusRecording: "● REC · SYTO SENSE",
  voiceStatusIdle: "○ SYTO SENSE",
  voiceTranscriptUploading: "Höre die Aufnahme...",
  voiceTranscriptRecording: "nehme auf...",
  voiceTranscriptIdle: "Mikrofon drücken und sprechen...",
  voiceHintLabel: "TIPP",
  voiceHintBody:
    'Sprich langsamer bei Zahlen und Mengen ("zweihundert Gramm"). Syto Sense kommt mit Sprache gut zurecht.',
  voiceUnsupported: "Dein Browser unterstützt das Aufnehmen nicht.",
  voiceCancelAria: "Abbrechen",
  voiceStopAria: "Aufnahme stoppen",
  voiceRecordAria: "Aufnehmen",
  voiceDoneAria: "Fertig",
  barcodeBackTitleScanning: "BARCODE",
  barcodeBackTitleLooking: "SUCHE",
  barcodeBackTitleManual: "EAN",
  barcodeBackTitleName: "NAME",
  barcodeBack: "Tagebuch",
  barcodeH1: "Scannen",
  barcodeLead: "Open Food Facts hat 3M+ Produkte. Wenn wir es nicht finden, gibst du es manuell ein.",
  barcodeStatusScanning: "● SCANNE · OFF API",
  barcodeEanLabel: "EAN-Code",
  barcodeEanPlaceholder: "z. B. 5901234567890",
  barcodePortionLabel: "Portion (Gramm)",
  barcodePortionHint:
    "Werte in der Datenbank gelten pro 100 g; hier gibst du ein, wie viel du wirklich gegessen hast.",
  barcodeCheck: "PRÜFEN",
  barcodeNameFallbackNote:
    "Diesen Code gibt es in keiner der Datenbanken. Gib den Produktnamen ein, die KI schätzt die Werte daraus.",
  barcodeNameLabel: "Produktname",
  barcodeNameHint: "Je konkreter, desto besser (Marke, Geschmack, Gewicht).",
  barcodeNamePlaceholder: "z. B. Naturjoghurt Piątnica 200 g",
  barcodeEstimating: "SCHÄTZE...",
  barcodeEstimate: "SCHÄTZEN →",
  barcodePortionMeta: "PORTION (G)",
  barcodePortionAria: "Portion in Gramm",
  barcodeLookingEyebrow: "Suche in der Datenbank",
  barcodeLookingSource: "Open Food Facts...",
  barcodeManualToggle: "EAN MANUELL EINGEBEN",
  barcodeScanToggle: "MIT KAMERA SCANNEN",
  barcodeUnsupportedHint: "Dein Browser unterstützt das Scannen nicht, gib es manuell ein.",
  barcodeEnterName: "Gib den Produktnamen ein",
  barcodeEstimateFailed: "Schätzung fehlgeschlagen. Versuch einen genaueren Namen.",
  barcodeGenericError: "Etwas ist schiefgelaufen",
  barcodeEanInvalid: "Ein EAN muss 8-14 Ziffern haben",
  barcodeCameraDenied: "Wir brauchen Zugriff auf die Kamera.",
  barcodeCameraError: "Kamera lässt sich nicht einschalten, gib den EAN manuell ein.",
  confirmBack: "Zurück",
  confirmTitle: "BESTÄTIGEN",
  confirmThingCount: (n) => (n === 1 ? "Sache" : "Sachen"),
  confirmCheckHint: " · prüf es, bevor du speicherst",
  confirmFavoriteError: "Speichern fehlgeschlagen",
  confirmGenericError: "Etwas ist schiefgelaufen",
  confirmFaved: "In Favoriten",
  confirmFaving: "Speichern...",
  confirmFavorite: "Favorit",
  confirmRemove: "Entfernen",
  confirmRemoveAria: (name) => `${name} entfernen`,
  confirmLessKcalAria: (name) => `Weniger kcal: ${name}`,
  confirmMoreKcalAria: (name) => `Mehr kcal: ${name}`,
  confirmEdit: "Bearbeiten",
  confirmEditDone: "Fertig",
  confirmFieldName: "Name",
  confirmFieldPortion: "Portion (g)",
  confirmFieldKcal: "Kcal",
  confirmFieldProtein: "Eiweiß (g)",
  confirmFieldFat: "Fett (g)",
  confirmFieldCarbs: "Kohlenh. (g)",
  confirmIngredientsLabel: "Zutaten",
  confirmDishSum: "Summe der Zutaten",
  confirmSwap: "Ersetzen",
  confirmSwapPlaceholder: "neue Zutat, z. B. Knäckebrot",
  confirmSwapApply: "Berechnen",
  confirmSwapping: "Berechne...",
  confirmSaving: "SPEICHERE...",
  confirmSaveCta: (n, kcal) =>
    `${n} ${n === 1 ? "POSITION" : "POSITIONEN"} SPEICHERN · ${kcal} KCAL`,
  confirmAddItem: "Fehlenden Eintrag hinzufügen",
  confirmAddItemLabel: "Was hinzufügen",
  confirmAddItemPlaceholder: "z. B. Kaffee mit Milch 200 ml",
  confirmAddItemSubmit: "Hinzufügen",
  confirmAddItemPending: "Wird hinzugefügt...",
  confirmAddItemCancel: "Abbrechen",
  confirmAddItemEmpty: "Kein Essen erkannt. Versuche etwas wie '150 g Reis'.",
  confirmAddItemError: "Etwas ist schiefgelaufen, bitte erneut versuchen",
  confirmAddItemMulti: (n) => `${n} Einträge hinzugefügt`,
  confirmAddItemLimit: "Hinzufüge-Limit erreicht. Speichere zuerst und ergänze den Rest danach.",
  confirmAddItemFallback: "Manuell eingeben",
  confirmAddIng: "Fehlende Zutat hinzufügen",
  confirmAddIngLabel: "Was zum Gericht ergänzen",
  confirmAddIngPlaceholder: "z. B. ein Löffel Öl, 50 g Käse",
  confirmAddIngSubmit: "Hinzufügen",
  confirmAddIngPending: "Wird hinzugefügt...",
  confirmAddIngCancel: "Abbrechen",
  confirmAddIngEmpty: "Zutat nicht erkannt. Versuche etwas wie '50 g Käse'.",
  confirmAddIngError: "Etwas ist schiefgelaufen, bitte erneut versuchen",
  confirmAddIngMulti: (n) => `${n} ${n === 1 ? "Zutat" : "Zutaten"} hinzugefügt`,
  confirmAddIngLimit: "Zutaten-Limit für dieses Gericht erreicht.",
  confirmAddIngFallback: "Manuell hinzufügen",
  confirmItemSwap: "Ersetzen",
  confirmItemSwapAria: (name) => `${name} durch etwas anderes ersetzen`,
  confirmItemSwapLabel: "Wodurch ersetzen",
  confirmItemSwapPlaceholder: "z. B. gekochte Eier 2 Stück",
  confirmItemSwapSubmit: "Berechnen",
  confirmItemSwapPending: "Berechne...",
  confirmItemSwapCancel: "Abbrechen",
  confirmItemSwapEmpty: "Kein Essen erkannt. Versuche etwas wie '150 g Reis'.",
  confirmItemSwapError: "Etwas ist schiefgelaufen, bitte erneut versuchen",
  confirmItemSwapFallback: "Manuell ersetzen",
  editTitle: "Mahlzeit korrigieren",
  editNameLabel: "Name",
  editKcalLabel: "Kalorien",
  editProteinLabel: "Eiweiß",
  editFatLabel: "Fett",
  editCarbsLabel: "Kohlenhydrate",
  editGramPlaceholder: "g",
  editGenericError: "Etwas ist schiefgelaufen",
  editSaving: "Speichern...",
  editSave: "Speichern",
  editCancel: "Abbrechen",
  inviteCodeLabel: "Dein Code",
  inviteCount: (n) => (n === 1 ? "Einladung" : "Einladungen"),
  inviteCopied: "Kopiert ✓",
  inviteShare: "Teilen ↗",
  inviteShareText: "Ich zähle Kalorien in Sytofood. Komm rein mit meinem Code:",
};

export const mealsCopy: Record<Lang, MealsCopy> = { pl, en, de };
